import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import ConsumerLayout from '../../components/consumer/ConsumerLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import { fetchConsumerDashboard, updateConsumerProfile, changeConsumerPassword } from '../../api/consumer';

// ─────────────────────────────────────────────
// Tiny helpers
// ─────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'pt', label: 'Portuguese' },
];

// ─────────────────────────────────────────────
// Toast notification (self-contained)
// ─────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div
      className={`fixed top-5 right-5 z-50 flex items-center gap-3 
        rounded-xl border px-4 py-3 shadow-lg text-[13px] font-medium
        transition-all duration-300
        ${isSuccess
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-red-50 border-red-200 text-red-800'
        }`}
    >
      {isSuccess ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )}
      {toast.message}
    </div>
  );
};

// ─────────────────────────────────────────────
// Reusable form field
// ─────────────────────────────────────────────
const Field = ({ label, error, children, hint }) => (
  <div>
    <label className="block text-[12px] font-medium text-slate-600 mb-1.5">{label}</label>
    {children}
    {hint && !error && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
  </div>
);

const inputCls = (err) =>
  `w-full px-3 py-2 rounded-lg border text-[13px] text-slate-800 
   bg-white placeholder:text-slate-300 outline-none transition
   focus:ring-1
   ${err
     ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
     : 'border-slate-200 focus:border-emerald-400 focus:ring-emerald-100'
   }`;

const selectCls = `w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-800 
  bg-white outline-none transition focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100`;

// ─────────────────────────────────────────────
// Toggle switch
// ─────────────────────────────────────────────
const Toggle = ({ checked, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <div>
      <p className="text-[13px] font-medium text-slate-700">{label}</p>
      {description && <p className="text-[11.5px] text-slate-400 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 
        ${checked ? 'bg-emerald-500' : 'bg-slate-200'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform duration-200
          ${checked ? 'translate-x-4.5' : 'translate-x-0.5'}`}
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
      />
    </button>
  </div>
);

// ─────────────────────────────────────────────
// Section Card wrapper
// ─────────────────────────────────────────────
const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-100">
      <h3 className="text-[14px] font-semibold text-slate-800">{title}</h3>
      {subtitle && <p className="text-[12px] text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ─────────────────────────────────────────────
// Password strength indicator
// ─────────────────────────────────────────────
const PasswordStrength = ({ password }) => {
  if (!password) return null;
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-emerald-500'];
  const textColors = ['', 'text-red-500', 'text-amber-500', 'text-blue-500', 'text-emerald-600'];
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300
            ${i <= score ? colors[score] : 'bg-slate-100'}`} />
        ))}
      </div>
      <p className={`text-[11px] font-medium ${textColors[score]}`}>
        {score > 0 ? `Password strength: ${labels[score]}` : ''}
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Page Component
// ─────────────────────────────────────────────
const ConsumerProfilePage = () => {
  const { user: authUser } = useAuth();

  // Remote data
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [toast, setToast] = useState(null);

  // Profile edit form
  const [profileForm, setProfileForm] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);

  // Preferences form
  const [prefForm, setPrefForm] = useState(null);
  const [prefSaving, setPrefSaving] = useState(false);

  // Password form
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdErrors, setPwdErrors] = useState({});
  const [pwdSaving, setPwdSaving] = useState(false);
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });

  // ── Fetch profile data ──
  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchConsumerDashboard();
      setProfileData(data);

      const pc = data?.user?.profile_consumer?.[0]
        ?? data?.user?.profile_consumer
        ?? {};
      const u = data?.user ?? {};

      setProfileForm({
        name: u.name ?? '',
        phone: pc.phone ?? '',
        bio: pc.bio ?? '',
        date_of_birth: pc.date_of_birth ? pc.date_of_birth.slice(0, 10) : '',
        gender: pc.gender ?? '',
        location: pc.location ?? '',
        country: pc.country ?? '',
      });

      setPrefForm({
        language: pc.language ?? 'en',
        timezone: pc.timezone ?? '',
        notification_email: pc.notification_email ?? true,
        notification_push: pc.notification_push ?? false,
        notification_sms: pc.notification_sms ?? false,
        marketing_emails: pc.marketing_emails ?? false,
        show_profile: pc.show_profile ?? true,
        show_email: pc.show_email ?? false,
      });
    } catch (err) {
      setFetchError(err?.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // ── Toast helper ──
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Profile form handler ──
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((p) => ({ ...p, [name]: value }));
    if (profileErrors[name]) {
      setProfileErrors((p) => ({ ...p, [name]: '' }));
    }
  };

  const validateProfile = () => {
    const errs = {};
    if (!profileForm.name?.trim()) errs.name = 'Name is required';
    else if (profileForm.name.trim().length < 2) errs.name = 'Name must be at least 2 characters';
    if (profileForm.bio && profileForm.bio.length > 500) errs.bio = 'Bio must be 500 characters or less';
    if (profileForm.phone && !/^[\d\s\-\+\(\)]{7,}$/.test(profileForm.phone))
      errs.phone = 'Enter a valid phone number';
    return errs;
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    const errs = validateProfile();
    if (Object.keys(errs).length) { setProfileErrors(errs); return; }

    try {
      setProfileSaving(true);
      await updateConsumerProfile(profileForm);
      showToast('Profile updated successfully');
      await loadProfile();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to update profile', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Preferences form handler ──
  const handlePrefToggle = (key) => {
    setPrefForm((p) => ({ ...p, [key]: !p[key] }));
  };

  const handlePrefChange = (e) => {
    const { name, value } = e.target;
    setPrefForm((p) => ({ ...p, [name]: value }));
  };

  const handlePrefSave = async (e) => {
    e.preventDefault();
    try {
      setPrefSaving(true);
      await updateConsumerProfile(prefForm);
      showToast('Preferences saved');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to save preferences', 'error');
    } finally {
      setPrefSaving(false);
    }
  };

  // ── Password form handler ──
  const handlePwdChange = (e) => {
    const { name, value } = e.target;
    setPwdForm((p) => ({ ...p, [name]: value }));
    if (pwdErrors[name]) setPwdErrors((p) => ({ ...p, [name]: '' }));
  };

  const validatePassword = () => {
    const errs = {};
    if (!pwdForm.current) errs.current = 'Current password is required';
    if (!pwdForm.next) errs.next = 'New password is required';
    else if (pwdForm.next.length < 8) errs.next = 'Minimum 8 characters required';
    if (!pwdForm.confirm) errs.confirm = 'Please confirm your new password';
    else if (pwdForm.next !== pwdForm.confirm) errs.confirm = 'Passwords do not match';
    if (pwdForm.current && pwdForm.next && pwdForm.current === pwdForm.next)
      errs.next = 'New password must differ from current password';
    return errs;
  };

  const handlePwdSubmit = async (e) => {
    e.preventDefault();
    const errs = validatePassword();
    if (Object.keys(errs).length) { setPwdErrors(errs); return; }

    try {
      setPwdSaving(true);
      await changeConsumerPassword({ currentPassword: pwdForm.current, newPassword: pwdForm.next });
      showToast('Password changed successfully');
      setPwdForm({ current: '', next: '', confirm: '' });
      setPwdErrors({});
      setShowPwdForm(false);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to change password';
      if (msg.toLowerCase().includes('current password')) {
        setPwdErrors({ current: 'Current password is incorrect' });
      } else {
        showToast(msg, 'error');
      }
    } finally {
      setPwdSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  const stats = profileData?.stats;
  const pc = profileData?.user?.profile_consumer?.[0]
    ?? profileData?.user?.profile_consumer
    ?? {};
  const u = profileData?.user ?? {};

  return (
    <ConsumerLayout title="My Profile">
      <Toast toast={toast} />

      <Breadcrumb
        items={[
          { label: 'Dashboard', href: '/consumer/dashboard' },
          { label: 'My Profile' },
        ]}
      />

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
        </div>
      )}

      {/* ── Fetch error ── */}
      {!loading && fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-[13px]">
          {fetchError}
        </div>
      )}

      {/* ── Content ── */}
      {!loading && !fetchError && profileForm && prefForm && (
        <div className="max-w-6xl space-y-5">

          {/* ── Account overview banner ── */}
          <div className="rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 flex items-center gap-5">
            <div className="h-14 w-14 shrink-0 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-xl font-bold text-white shadow-sm">
              {(u.name ?? authUser?.name ?? 'U').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-slate-800 truncate">{u.name ?? authUser?.name}</p>
              <p className="text-[12.5px] text-slate-500 mt-0.5">{u.email ?? authUser?.email}</p>
              <p className="text-[11.5px] text-slate-400 mt-1">
                Member since {formatDate(pc.joined_date ?? u.created_at ?? authUser?.created_at)}
                {stats?.totalReviews > 0 && (
                  <> &nbsp;·&nbsp; <span className="text-emerald-600 font-medium">{stats.totalReviews} review{stats.totalReviews !== 1 ? 's' : ''}</span></>
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,1fr)] gap-5 items-start">
            <div className="space-y-5">
              {/* ── 1. Profile Information ── */}
              <SectionCard
                title="Profile Information"
                subtitle="Your personal details visible to businesses and reviewers"
              >
                <form onSubmit={handleProfileSave} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Full Name *" error={profileErrors.name}>
                      <input
                        type="text"
                        name="name"
                        value={profileForm.name}
                        onChange={handleProfileChange}
                        className={inputCls(profileErrors.name)}
                        placeholder="Your full name"
                      />
                    </Field>

                    <Field label="Email Address">
                      <input
                        type="email"
                        value={u.email ?? authUser?.email ?? ''}
                        className={`${inputCls()} bg-slate-50 text-slate-400 cursor-not-allowed`}
                        readOnly
                        tabIndex={-1}
                      />
                      <p className="mt-1 text-[11px] text-slate-400">Email cannot be changed</p>
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Phone Number" error={profileErrors.phone}>
                      <input
                        type="tel"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleProfileChange}
                        className={inputCls(profileErrors.phone)}
                        placeholder="+91 98765 43210"
                      />
                    </Field>

                    <Field label="Date of Birth">
                      <input
                        type="date"
                        name="date_of_birth"
                        value={profileForm.date_of_birth}
                        onChange={handleProfileChange}
                        className={inputCls()}
                        max={new Date().toISOString().slice(0, 10)}
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Gender">
                      <select
                        name="gender"
                        value={profileForm.gender}
                        onChange={handleProfileChange}
                        className={selectCls}
                      >
                        {GENDER_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Location / City">
                      <input
                        type="text"
                        name="location"
                        value={profileForm.location}
                        onChange={handleProfileChange}
                        className={inputCls()}
                        placeholder="e.g. Mumbai"
                      />
                    </Field>
                  </div>

                  <Field label="Country">
                    <input
                      type="text"
                      name="country"
                      value={profileForm.country}
                      onChange={handleProfileChange}
                      className={inputCls()}
                      placeholder="e.g. India"
                    />
                  </Field>

                  <Field label="Bio" error={profileErrors.bio} hint="Max 500 characters">
                    <textarea
                      name="bio"
                      value={profileForm.bio}
                      onChange={handleProfileChange}
                      rows={3}
                      className={`${inputCls(profileErrors.bio)} resize-none`}
                      placeholder="Tell businesses a little about yourself..."
                    />
                    <p className="mt-1 text-right text-[11px] text-slate-400">
                      {(profileForm.bio ?? '').length} / 500
                    </p>
                  </Field>

                  <div className="flex justify-end pt-1">
                    <button
                      type="submit"
                      disabled={profileSaving}
                      className="flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed px-5 py-2 text-[13px] font-semibold text-white transition-colors"
                    >
                      {profileSaving ? (
                        <>
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Saving…
                        </>
                      ) : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </SectionCard>
            </div>

            <div className="space-y-5">
              {/* ── 2. Preferences ── */}
              <SectionCard
                title="Preferences"
                subtitle="Notification and privacy settings"
              >
                <form onSubmit={handlePrefSave} className="space-y-5">

                  {/* Language + Timezone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
                    <Field label="Language">
                  <select
                    name="language"
                    value={prefForm.language}
                    onChange={handlePrefChange}
                    className={selectCls}
                  >
                    {LANGUAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Timezone">
                  <input
                    type="text"
                    name="timezone"
                    value={prefForm.timezone}
                    onChange={handlePrefChange}
                    className={inputCls()}
                    placeholder="e.g. Asia/Kolkata"
                  />
                </Field>
              </div>

              {/* Notification toggles */}
              {/* <div>
                <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Notifications</p>
                <div className="rounded-lg border border-slate-100 overflow-hidden divide-y divide-slate-100">
                  <div className="px-4">
                    <Toggle
                      checked={prefForm.notification_email}
                      onChange={(v) => setPrefForm((p) => ({ ...p, notification_email: v }))}
                      label="Email Notifications"
                      description="Receive updates and review responses by email"
                    />
                  </div>
                  <div className="px-4">
                    <Toggle
                      checked={prefForm.notification_push}
                      onChange={(v) => setPrefForm((p) => ({ ...p, notification_push: v }))}
                      label="Push Notifications"
                      description="Browser push notifications for real-time alerts"
                    />
                  </div>
                  <div className="px-4">
                    <Toggle
                      checked={prefForm.notification_sms}
                      onChange={(v) => setPrefForm((p) => ({ ...p, notification_sms: v }))}
                      label="SMS Notifications"
                      description="Text message alerts for important updates"
                    />
                  </div>
                  <div className="px-4">
                    <Toggle
                      checked={prefForm.marketing_emails}
                      onChange={(v) => setPrefForm((p) => ({ ...p, marketing_emails: v }))}
                      label="Marketing Emails"
                      description="Promotions, features, and platform news"
                    />
                  </div>
                </div>
              </div> */}

              {/* Privacy toggles */}
              {/* <div>
                <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Privacy</p>
                <div className="rounded-lg border border-slate-100 overflow-hidden divide-y divide-slate-100">
                  <div className="px-4">
                    <Toggle
                      checked={prefForm.show_profile}
                      onChange={(v) => setPrefForm((p) => ({ ...p, show_profile: v }))}
                      label="Public Profile"
                      description="Allow others to view your profile page"
                    />
                  </div>
                  <div className="px-4">
                    <Toggle
                      checked={prefForm.show_email}
                      onChange={(v) => setPrefForm((p) => ({ ...p, show_email: v }))}
                      label="Show Email Publicly"
                      description="Display your email address on your public profile"
                    />
                  </div>
                </div>
              </div> */}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={prefSaving}
                      className="flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed px-5 py-2 text-[13px] font-semibold text-white transition-colors"
                    >
                      {prefSaving ? (
                        <>
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Saving…
                        </>
                      ) : 'Save Preferences'}
                    </button>
                  </div>
                </form>
              </SectionCard>

              {/* ── 3. Security ── */}
              <SectionCard
                title="Security"
                subtitle="Manage your account password"
              >
                {!showPwdForm ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">Password</p>
                      <p className="text-[12px] text-slate-400 mt-0.5">
                        Last updated: {formatDate(u.updated_at) ?? 'Never'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowPwdForm(true)}
                      className="flex items-center gap-2 px-4 py-2 text-[12.5px] font-semibold rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      Change Password
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handlePwdSubmit} className="space-y-4">

                {/* Current Password */}
                <Field label="Current Password *" error={pwdErrors.current}>
                  <div className="relative">
                    <input
                      type={showPwd.current ? 'text' : 'password'}
                      name="current"
                      value={pwdForm.current}
                      onChange={handlePwdChange}
                      className={`${inputCls(pwdErrors.current)} pr-10`}
                      placeholder="Enter your current password"
                      autoComplete="current-password"
                    />
                    <button type="button" tabIndex={-1}
                      onClick={() => setShowPwd((p) => ({ ...p, current: !p.current }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPwd.current
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.71-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                </Field>

                {/* New Password */}
                <Field label="New Password *" error={pwdErrors.next}>
                  <div className="relative">
                    <input
                      type={showPwd.next ? 'text' : 'password'}
                      name="next"
                      value={pwdForm.next}
                      onChange={handlePwdChange}
                      className={`${inputCls(pwdErrors.next)} pr-10`}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                    />
                    <button type="button" tabIndex={-1}
                      onClick={() => setShowPwd((p) => ({ ...p, next: !p.next }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPwd.next
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.71-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                  <PasswordStrength password={pwdForm.next} />
                </Field>

                {/* Confirm Password */}
                <Field label="Confirm New Password *" error={pwdErrors.confirm}>
                  <div className="relative">
                    <input
                      type={showPwd.confirm ? 'text' : 'password'}
                      name="confirm"
                      value={pwdForm.confirm}
                      onChange={handlePwdChange}
                      className={`${inputCls(pwdErrors.confirm)} pr-10`}
                      placeholder="Re-enter your new password"
                      autoComplete="new-password"
                    />
                    <button type="button" tabIndex={-1}
                      onClick={() => setShowPwd((p) => ({ ...p, confirm: !p.confirm }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPwd.confirm
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.71-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                  {/* Confirm match indicator */}
                  {pwdForm.confirm && !pwdErrors.confirm && pwdForm.next === pwdForm.confirm && (
                    <p className="mt-1 text-[11px] text-emerald-600 font-medium flex items-center gap-1">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      Passwords match
                    </p>
                  )}
                </Field>

                    <div className="flex gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={pwdSaving}
                        className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg text-[13px] font-semibold text-white transition-colors"
                      >
                        {pwdSaving ? (
                          <>
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Updating…
                          </>
                        ) : 'Update Password'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPwdForm(false);
                          setPwdForm({ current: '', next: '', confirm: '' });
                          setPwdErrors({});
                        }}
                        className="px-4 py-2 border border-slate-200 text-slate-600 text-[13px] font-medium rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </SectionCard>

              {/* ── 4. Account Stats (read-only) ── */}
              {stats && (
                <SectionCard
                  title="Account Overview"
                  subtitle="Your activity summary on VMC Reviews"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-2 gap-4">
                    {[
                      { label: 'Total Reviews', value: stats.totalReviews, color: 'text-blue-600' },
                      { label: 'Approved', value: stats.approvedReviews, color: 'text-emerald-600' },
                      { label: 'Avg Rating', value: stats.avgRating > 0 ? `${stats.avgRating} ★` : '–', color: 'text-amber-600' },
                      { label: 'Businesses', value: stats.uniqueBusinesses, color: 'text-violet-600' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-center">
                        <p className={`text-xl font-bold ${color}`}>{value}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Badges */}
                  {pc.badges?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Badges</p>
                      <div className="flex flex-wrap gap-2">
                        {pc.badges.map((badge) => (
                          <span key={badge} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11.5px] font-medium text-emerald-700">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                            {badge.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </SectionCard>
              )}
            </div>
          </div>

        </div>
      )}
    </ConsumerLayout>
  );
};

export default ConsumerProfilePage;
